import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { watchAPI, salesAPI, clientAPI } from '../services/api';
import { formatCurrencyShort } from '../utils/format';
import { TrendingUp, Package, Users, DollarSign, Plus, Receipt, UserPlus, Clock, ArrowRight, AlertTriangle, CheckCircle } from 'lucide-react';

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ watches: null, sales: null, clients: null });
  const [recentSales, setRecentSales] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    fetchRecentSales();
  }, []);

  const fetchStats = async () => {
    try {
      const [watchRes, salesRes, clientRes] = await Promise.all([
        watchAPI.getStats(),
        salesAPI.getStats(),
        clientAPI.getStats(),
      ]);
      setStats({ watches: watchRes.data, sales: salesRes.data, clients: clientRes.data });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentSales = async () => {
    try {
      const response = await salesAPI.getAll({ page: 1, per_page: 5 });
      setRecentSales(response.data.sales || []);
    } catch (error) {
      console.error('Error fetching recent sales:', error);
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
      </div>
    );
  }

  const statCards = [
    { title: 'Total Inventory', value: stats.watches?.total_watches ?? 0, subtitle: `${stats.watches?.available_watches ?? 0} available`, icon: Package, onClick: () => navigate('/inventory') },
    { title: 'Inventory Value', value: formatCurrencyShort(stats.watches?.total_inventory_value ?? 0), subtitle: 'Total assets', icon: TrendingUp, onClick: () => navigate('/inventory') },
    { title: 'Total Clients', value: stats.clients?.total_clients ?? 0, subtitle: 'Active relationships', icon: Users, onClick: () => navigate('/clients') },
    { title: 'Total Profit', value: formatCurrencyShort(Number(stats.sales?.total_profit) || 0), subtitle: `${(Number(stats.sales?.avg_profit_margin) || 0).toFixed(1)}% margin`, icon: DollarSign, onClick: () => navigate('/sales') },
  ];

  const isEmpty = !stats.watches?.total_watches && !stats.sales?.total_sales && !stats.clients?.total_clients;

  return (
    <div className="page-container space-y-6 md:space-y-8">
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <p className="page-subtitle">Overview of your watch dealing business</p>
      </div>

      {isEmpty && (
        <div className="card card-body border-[#e5e5e5] bg-[#fafafa]">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="p-3 rounded-xl bg-[#e5e5e5]">
              <Package className="w-8 h-8 text-[#737373]" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-[#0a0a0a] mb-0.5">Welcome to Dial-Grid</h3>
              <p className="text-sm text-[#737373]">Add your first watch, then record a sale or add a client to see your dashboard.</p>
            </div>
            <button type="button" onClick={() => navigate('/inventory/add')} className="btn-primary whitespace-nowrap">
              Add your first watch
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <button type="button" onClick={() => navigate('/sales/record')} className="card card-body flex items-center gap-3 hover:bg-[#fafafa] transition-colors btn-success text-left">
          <Receipt className="w-5 h-5 flex-shrink-0" />
          Record Sale
        </button>
        <button type="button" onClick={() => navigate('/inventory/add')} className="card card-body flex items-center gap-3 bg-[#0a0a0a] text-white font-medium rounded-xl hover:bg-[#171717] transition-colors text-left">
          <Plus className="w-5 h-5 flex-shrink-0" />
          Add Watch
        </button>
        <button type="button" onClick={() => navigate('/clients/add')} className="card card-body flex items-center gap-3 hover:bg-[#fafafa] transition-colors btn-primary text-left">
          <UserPlus className="w-5 h-5 flex-shrink-0" />
          Add Client
        </button>
        <button type="button" onClick={() => navigate('/inventory')} className="card card-body flex items-center gap-3 hover:bg-[#fafafa] transition-colors btn-secondary text-left">
          <Clock className="w-5 h-5 flex-shrink-0" />
          View Inventory
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <div
            key={index}
            role="button"
            tabIndex={0}
            onClick={stat.onClick}
            onKeyDown={(e) => e.key === 'Enter' && stat.onClick()}
            className="card card-body cursor-pointer hover:bg-[#fafafa] transition-colors"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="p-2 rounded-lg bg-[#f5f5f5]">
                <stat.icon className="w-5 h-5 text-[#737373]" />
              </div>
            </div>
            <div className="text-2xl font-semibold text-[#0a0a0a] tracking-tight">{stat.value}</div>
            <div className="text-sm font-medium text-[#737373]">{stat.title}</div>
            {stat.subtitle && <div className="text-xs text-[#a3a3a3] mt-0.5">{stat.subtitle}</div>}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card card-body">
          <h3 className="text-base font-semibold text-[#0a0a0a] mb-4">Key Metrics</h3>
          <div className="space-y-2">
            <div className="flex justify-between items-center py-3 px-4 rounded-lg bg-[#fafafa]">
              <span className="text-sm font-medium text-[#737373]">Avg Profit/Sale</span>
              <span className="font-semibold text-[#0a0a0a]">${(Number(stats.sales?.avg_profit) || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center py-3 px-4 rounded-lg bg-[#fafafa]">
              <span className="text-sm font-medium text-[#737373]">Profit Margin</span>
              <span className="font-semibold text-[#0a0a0a]">{(Number(stats.sales?.avg_profit_margin) || 0).toFixed(1)}%</span>
            </div>
            <div className="flex justify-between items-center py-3 px-4 rounded-lg bg-[#f0fdf4]">
              <span className="text-sm font-medium text-[#737373]">Total Sales</span>
              <span className="font-semibold text-[#0a0a0a]">{stats.sales?.total_sales ?? 0}</span>
            </div>
          </div>
        </div>
        <div className="card card-body">
          <h3 className="text-base font-semibold text-[#0a0a0a] mb-4">Quick Stats</h3>
          <div className="space-y-2">
            <div className="flex justify-between items-center py-3 px-4 rounded-lg bg-[#fafafa]">
              <span className="text-sm font-medium text-[#737373]">Total Revenue</span>
              <span className="font-semibold text-[#0a0a0a]">${(Number(stats.sales?.total_revenue) || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center py-3 px-4 rounded-lg bg-[#fafafa]">
              <span className="text-sm font-medium text-[#737373]">Highest Sale</span>
              <span className="font-semibold text-[#0a0a0a]">${(Number(stats.sales?.highest_sale) || 0).toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center py-3 px-4 rounded-lg bg-[#fafafa]">
              <span className="text-sm font-medium text-[#737373]">Avg Sale Price</span>
              <span className="font-semibold text-[#0a0a0a]">${(Number(stats.sales?.avg_sale_price) || 0).toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card card-body">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-[#0a0a0a]">Recent Sales</h3>
            <button type="button" onClick={() => navigate('/sales')} className="text-sm font-medium text-[#0a0a0a] hover:text-[#737373] flex items-center gap-1">
              View all <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          {recentSales.length > 0 ? (
            <div className="space-y-2">
              {recentSales.map((sale) => (
                <div
                  key={sale.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/sales/${sale.id}`)}
                  onKeyDown={(e) => e.key === 'Enter' && navigate(`/sales/${sale.id}`)}
                  className="p-3 rounded-lg bg-[#fafafa] hover:bg-[#f5f5f5] cursor-pointer transition-colors border border-transparent hover:border-[#e5e5e5]"
                >
                  <div className="flex justify-between items-center mb-0.5">
                    <span className="font-medium text-[#0a0a0a]">{sale.watch_brand} {sale.watch_model}</span>
                    <span className="font-semibold text-[#16a34a]">${(Number(sale.sale_price) || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-xs text-[#737373]">
                    <span>{sale.client_name || 'Walk-in'}</span>
                    <span>{new Date(sale.sale_date || sale.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Receipt className="w-10 h-10 text-[#d4d4d4] mx-auto mb-2" />
              <p className="text-sm text-[#737373] mb-3">No sales yet</p>
              <button type="button" onClick={() => navigate('/sales/record')} className="btn-success">
                Record your first sale
              </button>
            </div>
          )}
        </div>

        <div className="card card-body">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-[#0a0a0a]">Inventory Status</h3>
            <button type="button" onClick={() => navigate('/inventory')} className="text-sm font-medium text-[#0a0a0a] hover:text-[#737373] flex items-center gap-1">
              View all <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-2">
            <div className="p-4 rounded-lg bg-[#f0fdf4] border border-[#bbf7d0] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#16a34a] rounded-lg"><CheckCircle className="w-4 h-4 text-white" /></div>
                <div>
                  <p className="font-medium text-[#0a0a0a]">Available</p>
                  <p className="text-xs text-[#737373]">Ready for sale</p>
                </div>
              </div>
              <span className="text-xl font-semibold text-[#16a34a]">{stats.watches?.available_watches ?? 0}</span>
            </div>
            <div className="p-4 rounded-lg bg-[#fafafa] border border-[#e5e5e5] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#737373] rounded-lg"><Package className="w-4 h-4 text-white" /></div>
                <div>
                  <p className="font-medium text-[#0a0a0a]">Sold</p>
                  <p className="text-xs text-[#737373]">Completed</p>
                </div>
              </div>
              <span className="text-xl font-semibold text-[#0a0a0a]">{(stats.watches?.total_watches ?? 0) - (stats.watches?.available_watches ?? 0)}</span>
            </div>
            {(stats.watches?.available_watches ?? 0) > 0 && (stats.watches?.available_watches ?? 0) < 5 && (
              <div className="p-4 rounded-lg bg-[#fffbeb] border border-[#fde68a] flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[#d97706] rounded-lg"><AlertTriangle className="w-4 h-4 text-white" /></div>
                  <div>
                    <p className="font-medium text-[#0a0a0a]">Low inventory</p>
                    <p className="text-xs text-[#737373]">Add more watches</p>
                  </div>
                </div>
                <button type="button" onClick={() => navigate('/inventory/add')} className="btn-primary text-sm py-2">Add watch</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
